import React from 'react';
import { useParams } from "react-router-dom";
import { useQuery } from 'react-query';
import { useEnergyApi } from '../_api/EnergyApiContext';
import PropTypes from 'prop-types';
import { PropTypes as MobXPropTypes } from 'mobx-react';

import RecRegistryCertificateGeneration from '../_api/data/RecRegistryCertificateGeneration'
import RecRegistryCertificateBundlesDataTable from './RecRegistryCertificateBundlesDataTable'
import RecRegistryActionsDataTable, { ActionsFilterData, ActionsFilterForm } from './RecRegistryActionsDataTable'

import { Alert } from '@material-ui/lab';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Badge from '@material-ui/core/Badge';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
}));

interface Params {
  generationId: string
}

const CertificateGenerationPage: React.FC = (): React.ReactElement => {
  const { generationId } = useParams<Params>();
  const energyApi = useEnergyApi();
  const query = async (): Promise<RecRegistryCertificateGeneration | undefined> => {
    return await energyApi.getRecRegistryCertificateGeneration(generationId)
  }
  const {
    isLoading,
    isError,
    error,
    data,
    isFetching,
    isPreviousData,
  } = useQuery<RecRegistryCertificateGeneration | undefined, Error>(['certificateGeneration', generationId], (query), { keepPreviousData: false });
  const [filter, setFilter] = React.useState({} as ActionsFilterData)

  if (data) {
    return (
      <>
        <GenerationHeader generation={data} filter={filter} />
        <ActionsFilterForm filter={filter} setFilter={setFilter} />
        <GenerationTabs generation={data} filter={filter} />
      </>
    )
  }
  else {
    return <Alert severity="error">Generation ID {generationId} does not exist!</Alert>
  }
}


type GenerationHeaderProps = {
  generation: RecRegistryCertificateGeneration
  filter: ActionsFilterData
}

const GenerationHeader: React.FC<GenerationHeaderProps> = ({ generation }): React.ReactElement => {
  return (
    <>
      <h1>{generation.id}</h1>
      {  generation.containsErrors
        && <Alert severity="error">There are action errors for this generation!</Alert>
      }
      {  generation.processingRequired
        ? <Alert severity="warning">There are actions awaiting processing.</Alert>
        : <Alert severity="success">Up to date.</Alert>
      }
      { generation.actionDayImported
        && <Typography>Action imported: {generation.actionDayImported}</Typography>
      }
      { generation.actionDayProcessed
        && <Typography>Action processed: {generation.actionDayProcessed}</Typography>
      }
    </>
  )
}

GenerationHeader.propTypes = {
  generation: MobXPropTypes.observableObject.isRequired,
  filter: PropTypes.object.isRequired
}

// Tabs

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

enum SelectedTab {
  bundles = "/bundles",
  actions = "/actions",
}

const GenerationTabs: React.FC<GenerationHeaderProps> = ({ generation, filter }): React.ReactElement => {
  const classes = useStyles();
  const [value, setValue] = React.useState(SelectedTab.bundles);

  const handleChange = (_event: React.ChangeEvent<unknown>, newValue: SelectedTab) => {
    setValue(newValue);
  };

  return (
    <div className={classes.root}>
      <AppBar position="static" color="default">
        <Tabs value={value} onChange={handleChange} aria-label="simple tabs example" textColor="primary" indicatorColor="primary">
          <Tab label="Bundles" value="/bundles" {...a11yProps(0)} />
          <Tab label="Actions" value="/actions" {...a11yProps(1)} />
        </Tabs>
      </AppBar>
      <TabPanel value={value} index={SelectedTab.bundles}>
        <RecRegistryCertificateBundlesDataTable generationId={generation.id} />
      </TabPanel>
      <TabPanel value={value} index={SelectedTab.actions}>
        <RecRegistryActionsDataTable generationId={generation.id} serialNumber={filter.serialNumber} />
      </TabPanel>

    </div>
  )
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

GenerationTabs.propTypes = {
  generation: MobXPropTypes.observableObject.isRequired,
  filter: PropTypes.object.isRequired
}

export default CertificateGenerationPage
